import L from "leaflet"

export class HuntingAreasRenderer {
  constructor(map, dataFetcher) {
    this.map = map;
    this.dataFetcher = dataFetcher;
    this.huntingAreasLayer = null;
    this.huntingAreasData = new Map(); // Store hunting areas by ID to avoid duplicates
  }

  async loadAndRender(huntingAreasUrl) {
    try {
      const huntingAreas = await this.dataFetcher.fetchHuntingAreasData(huntingAreasUrl);
      
      if (huntingAreas && huntingAreas.length > 0) {
        this.addHuntingAreas(huntingAreas);
      }
    } catch (error) {
      console.error('Error loading hunting areas:', error);
    }
  }

  addHuntingAreas(huntingAreas) {
    // Initialize layer if it doesn't exist
    if (!this.huntingAreasLayer) {
      this.huntingAreasLayer = L.layerGroup().addTo(this.map);
    }

    // Process new hunting areas
    huntingAreas.forEach(area => {
      // Skip if we already have this hunting area
      if (this.huntingAreasData.has(area.id)) {
        return;
      }

      // Validate geometry
      if (!this.isValidGeometry(area.geometry)) {
        return;
      }

      // Store the area data
      this.huntingAreasData.set(area.id, area);

      // Create and add the polygon
      const polygon = this.createHuntingAreaPolygon(area);
      if (polygon) {
        this.huntingAreasLayer.addLayer(polygon);
      }
    });
  }

  isValidGeometry(geometry) {
    return geometry && 
      geometry.rings && 
      Array.isArray(geometry.rings) &&
      geometry.rings.length > 0 &&
      geometry.rings[0] &&
      Array.isArray(geometry.rings[0]) &&
      geometry.rings[0].length > 2; // At least 3 points for a polygon
  }

  createHuntingAreaPolygon(area) {
    const geoJson = this.convertArcGISToGeoJSON(area.geometry);
    if (!geoJson) return null;

    const polygon = L.geoJSON(geoJson, {
      style: () => this.getHuntingAreaStyle(area),
      onEachFeature: (feature, layer) => {
        // Store hunting area data on the layer for event handlers
        layer.huntingAreaData = area;
        
        // Use HTML from server if available, otherwise create simple popup
        const popupContent = area.html || this.createHuntingAreaPopup(area);
        layer.bindPopup(popupContent);
        
        layer.on({
          mouseover: (e) => this.highlightFeature(e),
          mouseout: (e) => this.resetHighlight(e)
        });
      }
    });

    return polygon;
  }

  // Convert ArcGIS geometry format to standard GeoJSON
  convertArcGISToGeoJSON(arcgisGeometry) {
    if (!arcgisGeometry || !arcgisGeometry.rings || !Array.isArray(arcgisGeometry.rings)) {
      console.warn('Invalid ArcGIS geometry:', arcgisGeometry);
      return null;
    }

    try {
      // ArcGIS uses 'rings' array, GeoJSON uses 'coordinates' array
      const coordinates = arcgisGeometry.rings.map(ring => {
        if (!Array.isArray(ring) || ring.length < 3) {
          console.warn('Invalid ring in geometry:', ring);
          return null;
        }
        
        // Ensure ring is closed (first point equals last point)
        const validRing = [...ring];
        if (validRing.length > 0) {
          const first = validRing[0];
          const last = validRing[validRing.length - 1];
          
          if (first[0] !== last[0] || first[1] !== last[1]) {
            validRing.push([...first]);
          }
        }
        
        return validRing;
      }).filter(ring => ring !== null);

      if (coordinates.length === 0) {
        console.warn('No valid rings found in geometry');
        return null;
      }

      return {
        type: 'Polygon',
        coordinates: coordinates
      };
    } catch (error) {
      console.error('Error converting ArcGIS geometry to GeoJSON:', error, arcgisGeometry);
      return null;
    }
  }

  getHuntingAreaStyle(area) {
    const status = area.status;
    
    const styles = {
      'državno': {
        fillColor: '#2563eb',
        color: '#1d4ed8',
        weight: 1,
        opacity: 0.6,
        fillOpacity: 0.2
      },
      'privatno': {
        fillColor: '#dc2626',
        color: '#b91c1c',
        weight: 1,
        opacity: 0.6,
        fillOpacity: 0.2
      },
      'javno': {
        fillColor: '#16a34a',
        color: '#15803d',
        weight: 1,
        opacity: 0.6,
        fillOpacity: 0.2
      },
      'default': {
        fillColor: '#6b7280',
        color: '#4b5563',
        weight: 1,
        opacity: 0.6,
        fillOpacity: 0.2
      }
    };

    return styles[status] || styles.default;
  }

  createHuntingAreaPopup(area) {
    return `
      <div class="hunting-area-popup">
        <h6 class="fw-bold mb-1">${area.name}</h6>
        <div class="small">
          <div><strong>Status:</strong> ${area.status}</div>
          <div><strong>Županija:</strong> ${area.zupanija}</div>
          ${area.area_ha ? `<div><strong>Površina:</strong> ${area.area_ha.toLocaleString()} ha</div>` : ''}
        </div>
      </div>
    `;
  }

  highlightFeature(e) {
    const layer = e.target;
    layer.setStyle({
      weight: 2,
      opacity: 1,
      fillOpacity: 0.4
    });
  }

  resetHighlight(e) {
    const layer = e.target;
    // Get the hunting area data stored on the layer
    const huntingAreaData = layer.huntingAreaData;
    
    if (huntingAreaData) {
      // Reset to original style
      const originalStyle = this.getHuntingAreaStyle(huntingAreaData);
      layer.setStyle(originalStyle);
    }
  }

  show() {
    if (this.huntingAreasLayer && !this.map.hasLayer(this.huntingAreasLayer)) {
      this.huntingAreasLayer.addTo(this.map);
    }
  }

  hide() {
    if (this.huntingAreasLayer && this.map.hasLayer(this.huntingAreasLayer)) {
      this.map.removeLayer(this.huntingAreasLayer);
    }
  }

  remove() {
    if (this.huntingAreasLayer) {
      this.map.removeLayer(this.huntingAreasLayer);
      this.huntingAreasLayer = null;
      this.huntingAreasData.clear();
    }
  }

  clearExistingData() {
    this.huntingAreasData.clear();
    if (this.huntingAreasLayer) {
      this.huntingAreasLayer.clearLayers();
    }
  }
}
